import type { PGD } from "../types";

export const pgdPenvSoreThroat: PGD = {
  id: "pgd-5a-penv-sorethroat",
  reference: "5a",
  version: "1.0",
  drugRef: "phenoxymethylpenicillin",
  indication:
    "Acute sore throat due to suspected streptococcal infection — no penicillin allergy",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",
  population: {
    ageRange: { min: 5, max: 999 },
    pregnancyAllowed: true,
    breastfeedingAllowed: true,
    notes: "Only 250mg tablets exist — no 500mg tablet. 500mg dose requires two 250mg tablets.",
  },
  duration: { days: 5 },
  doseBands: [
    {
      id: "penv-age5",
      resolveBy: "age",
      ageRange: { min: 5, max: 5 },
      dose: 125,
      frequency: "FOUR times a day",
      comment: "Age 5 — liquid only",
      formulations: [
        {
          formulationId: "penv-125-5ml",
          instruction: "Take 5mL (125mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
        {
          formulationId: "penv-250-5ml",
          instruction: "Take 2.5mL (125mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
      ],
    },
    {
      id: "penv-age6-11",
      resolveBy: "age",
      ageRange: { min: 6, max: 11 },
      dose: 250,
      frequency: "FOUR times a day",
      formulations: [
        {
          formulationId: "penv-250-tab",
          instruction: "Take ONE 250mg tablet FOUR times a day for 5 days",
          quantity: { count: 20, label: "20 tablets" },
        },
        {
          formulationId: "penv-250-5ml",
          instruction: "Take 5mL (250mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
        {
          formulationId: "penv-125-5ml",
          instruction: "Take 10mL (250mg) FOUR times a day for 5 days",
          quantity: { bottles: 2, label: "2 × 100mL bottles" },
        },
      ],
    },
    {
      id: "penv-age12plus",
      resolveBy: "age",
      ageRange: { min: 12, max: 999 },
      dose: 500,
      frequency: "FOUR times a day",
      comment: "500mg dose uses TWO 250mg tablets — no 500mg tablet exists",
      formulations: [
        {
          formulationId: "penv-250-tab",
          instruction: "Take TWO 250mg tablets FOUR times a day for 5 days",
          quantity: { count: 40, label: "40 tablets" },
        },
        {
          formulationId: "penv-250-5ml",
          instruction: "Take 10mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 2, label: "2 × 100mL bottles" },
        },
        {
          formulationId: "penv-125-5ml",
          instruction: "Take 20mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 4, label: "4 × 100mL bottles" },
        },
      ],
    },
  ],
  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    {
      id: "ex-unable-swallow",
      question:
        "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?",
    },
    {
      id: "ex-failed-abx",
      question: "Failed previous antibiotic for this episode of sore throat?",
    },
    {
      id: "ex-recurrent-tonsil",
      question: "Recurrent sore throat/tonsillitis?",
      hint: "7+ significant episodes in preceding 12 months, or 5+ in each of preceding 2 years, or 3+ in each of preceding 3 years",
    },
    {
      id: "ex-tonsillectomy",
      question: "Previous tonsillectomy or post-throat surgery/procedure?",
    },
    {
      id: "ex-long-term-penv",
      question: "Currently on long-term phenoxymethylpenicillin?",
      hint: "e.g. prophylaxis in asplenia",
    },
    { id: "ex-keto", question: "Ketogenic diet?" },
    {
      id: "ex-ckd-severe",
      question: "CKD stage 4 or 5 (eGFR <30 mL/min/1.73m²)?",
    },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-methotrexate", question: "Currently taking methotrexate?" },
    { id: "int-probenecid", question: "Currently taking probenecid?" },
    {
      id: "int-agranulocytosis",
      question: "Taking agranulocytosis-risk medicines?",
      hint: "methotrexate, sulfasalazine, carbimazole, propylthiouracil, cotrimoxazole, valganciclovir, clozapine, carbamazepine, chemotherapy",
    },
  ],
  cautions: [
    {
      id: "caution-breastfeeding",
      text: "Breastfeeding — monitor nursing infant for GI disturbances, nausea, hypersensitivity, rashes, oral candida infection",
    },
    {
      id: "caution-warfarin",
      text: "Coumarin anticoagulants — INR may rise. Advise INR monitoring and to seek medical attention if bleeding develops.",
    },
    {
      id: "caution-excipients",
      text: "Check excipients: lactose, sucrose, fructose, sorbitol; aspartame in phenylketonuria",
    },
  ],
  counselling: [
    "Take on an empty stomach — 30 minutes before food or 2 hours after food",
    "Tablets should be swallowed whole",
    "Complete the full 5-day course even if symptoms improve",
    "Return to pharmacy if symptoms worsen rapidly at any time",
  ],
  counsellingPoints: [
    { text: "Take on an empty stomach — 30 minutes before food or 2 hours after food" },
    { text: "Tablets should be swallowed whole" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    {
      text: "Acute sore throat can last around 1 week — most people will get better without antibiotics regardless of cause",
    },
    { text: "Paracetamol or ibuprofen (OTC) can be used for pain and/or fever" },
    {
      text: "Medicated lozenges and throat sprays may help with pain but adverse effects are common",
    },
    { text: "Return to pharmacy if symptoms worsen rapidly at any time" },
    { text: "Return if no improvement after completing the course" },
    { text: "Seek urgent help if signs of sepsis develop — call 999 or attend A&E" },
    {
      text: "Children should be encouraged to swallow solid oral dose forms where possible",
      linkRefs: [
        "medicines-for-children-penv",
        "medicines-for-children-giving-tablets",
      ],
    },
  ],
  outcomeLinkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
};
